import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ALERT_LEVEL_OPTIONS = [
  { value: 'info', label: 'Info', urgency: 'Informational', color: '#2563EB' },
  { value: 'warning', label: 'Warning', urgency: 'Low', color: '#EAB308' },
  { value: 'serious', label: 'Serious', urgency: 'High', color: '#EA580C' },
  { value: 'critical', label: 'Critical', urgency: 'Immediate', color: '#DC2626' },
];

/** Which Call QA alert levels send an email, and to whom. */
const AlertEmailSettingsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['callQaAlertEmailSettings'],
    queryFn: () => apiClient.callQa.getAlertEmailSettings(),
  });
  const [levels, setLevels] = useState<string[]>([]);
  const [recipients, setRecipients] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const d = data?.data?.data;
    if (!d) return;
    const nextLevels = Array.isArray(d.levels) ? d.levels : [];
    const nextRecipients = (d.recipients || []).join(', ');
    setLevels((prev) => (
      prev.length === nextLevels.length && prev.every((v, i) => v === nextLevels[i])
        ? prev
        : nextLevels
    ));
    setRecipients((prev) => (prev === nextRecipients ? prev : nextRecipients));
  }, [data]);

  const save = useMutation({
    mutationFn: () => apiClient.callQa.updateAlertEmailSettings({
      levels,
      recipients: recipients.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaAlertEmailSettings'] });
      setSaved(true);
    },
  });

  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <FormGroup row>
        {ALERT_LEVEL_OPTIONS.map((o) => (
          <FormControlLabel
            key={o.value}
            control={
              <Checkbox
                checked={levels.includes(o.value)}
                onChange={(e) => {
                  setSaved(false);
                  setLevels(e.target.checked ? [...levels, o.value] : levels.filter((l) => l !== o.value));
                }}
                sx={{ color: o.color, '&.Mui-checked': { color: o.color } }}
              />
            }
            label={<span><strong>{o.label}</strong> <Typography component="span" variant="caption" color="text.secondary">· urgency {o.urgency.toLowerCase()}</Typography></span>}
          />
        ))}
      </FormGroup>
      {levels.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>No level selected — no alert emails will be sent.</Alert>}
      <TextField
        fullWidth
        size="small"
        label="Recipients"
        value={recipients}
        onChange={(e) => { setSaved(false); setRecipients(e.target.value); }}
        helperText="Comma-separated emails. One email per call; it is sent again only if the call escalates to a higher level."
        sx={{ mt: 2 }}
      />
      <Button variant="contained" sx={{ mt: 1.5 }} disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save alert emails'}
      </Button>
      {saved && <Alert severity="success" sx={{ mt: 1 }}>Alert email settings saved.</Alert>}
      {save.isError && <Alert severity="error" sx={{ mt: 1 }}>Could not save the alert email settings.</Alert>}
    </Box>
  );
};

/**
 * Agent Conduct email controls — independent of QA scorecards and of the Alert emails above.
 * Master + per-severity switches, recipients, and the legacy scorecard-alert gate.
 */
const ConductEmailSettingsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['callQaConductSettings'],
    queryFn: () => apiClient.callQa.getConductSettings(),
  });
  const [master, setMaster] = useState(true);
  const [warning, setWarning] = useState(false);
  const [serious, setSerious] = useState(true);
  const [critical, setCritical] = useState(true);
  const [legacy, setLegacy] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const d = data?.data?.data;
    if (!d) return;
    setMaster(Boolean(d.master_enabled));
    setWarning(Boolean(d.warning_enabled));
    setSerious(Boolean(d.serious_enabled));
    setCritical(Boolean(d.critical_enabled));
    setLegacy(Boolean(d.legacy_alert_emails_enabled));
    setRecipients((d.recipients || []).join(', '));
  }, [data]);

  const save = useMutation({
    mutationFn: () => apiClient.callQa.updateConductSettings({
      master_enabled: master,
      warning_enabled: warning,
      serious_enabled: serious,
      critical_enabled: critical,
      legacy_alert_emails_enabled: legacy,
      recipients: recipients.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaConductSettings'] });
      setSaved(true);
    },
  });

  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={master} onChange={(e) => { setSaved(false); setMaster(e.target.checked); }} />}
        label={<strong>Conduct emails enabled (master)</strong>}
      />
      {!master && <Alert severity="info" sx={{ mb: 1 }}>Master is OFF — no conduct emails are sent. Detection, incidents, and the Conduct dashboard continue.</Alert>}
      <FormGroup row sx={{ opacity: master ? 1 : 0.5, pointerEvents: master ? 'auto' : 'none' }}>
        <FormControlLabel
          control={<Checkbox checked={warning} onChange={(e) => { setSaved(false); setWarning(e.target.checked); }} />}
          label="Warning"
        />
        <FormControlLabel
          control={<Checkbox checked={serious} onChange={(e) => { setSaved(false); setSerious(e.target.checked); }} />}
          label="Serious"
        />
        <FormControlLabel
          control={<Checkbox checked={critical} onChange={(e) => { setSaved(false); setCritical(e.target.checked); }} />}
          label="Critical"
        />
      </FormGroup>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Each severity is independent. Turning a severity off never disables detection, incident storage, or reporting.
        At most one consolidated email is sent per call.
      </Typography>
      <TextField
        fullWidth
        size="small"
        label="Conduct email recipients"
        value={recipients}
        onChange={(e) => { setSaved(false); setRecipients(e.target.value); }}
        helperText="Comma-separated emails."
        sx={{ mt: 1 }}
      />
      <FormControlLabel
        sx={{ mt: 1.5, display: 'block' }}
        control={<Switch checked={legacy} onChange={(e) => { setSaved(false); setLegacy(e.target.checked); }} />}
        label="Re-enable legacy scorecard/compliance alert emails"
      />
      <Alert severity={legacy ? 'warning' : 'info'} sx={{ mb: 1 }}>
        {legacy
          ? 'Legacy alert emails are ON — scorecard/compliance findings will again email QA (the old multi-notification behavior).'
          : 'Legacy alert emails are OFF (recommended). Scorecard/compliance findings no longer email QA; only Agent Conduct does.'}
      </Alert>
      <Button variant="contained" sx={{ mt: 0.5 }} disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save conduct emails'}
      </Button>
      {saved && <Alert severity="success" sx={{ mt: 1 }}>Conduct email settings saved.</Alert>}
      {save.isError && <Alert severity="error" sx={{ mt: 1 }}>Could not save conduct email settings.</Alert>}
    </Box>
  );
};

const CallMonitoringGlobalConfigCard: React.FC = () => {
  const { user } = useAuth();
  const canConfigure = (user?.permissions || []).includes('call_qa.call_monitoring.configure')
    || (user?.permissions || []).includes('call_qa.configure')
    || user?.role === 'super_user'
    || user?.role === 'administrator';
  const queryClient = useQueryClient();
  const [allowlistText, setAllowlistText] = useState('');
  const [localEnabled, setLocalEnabled] = useState(false);
  const [captureMode, setCaptureMode] = useState('DESKTOP_CAPTURE');
  const [appMode, setAppMode] = useState<'ALL' | 'ALLOWLIST'>('ALL');
  const [consent, setConsent] = useState(true);
  const [recordingAllowed, setRecordingAllowed] = useState(true);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['callMonitoringConfig'],
    queryFn: () => apiClient.monitoring.getCallMonitoringConfig(),
    enabled: canConfigure,
  });

  useEffect(() => {
    const cfg = data?.data?.data;
    if (!cfg || Array.isArray(cfg.rows)) return;
    const nextEnabled = Boolean(cfg.enabled);
    const nextCapture = cfg.captureMode || cfg.capture_mode || 'DESKTOP_CAPTURE';
    const mode = String(cfg.callRecordingAppMode || cfg.call_recording_app_mode || 'ALL').toUpperCase();
    const nextAppMode = mode === 'ALLOWLIST' ? 'ALLOWLIST' as const : 'ALL' as const;
    const nextConsent = cfg.recordingConsentConfirmed !== false && cfg.recording_consent_confirmed !== false;
    const nextRecordingAllowed = cfg.recordingAllowed !== false && cfg.recording_allowed !== false;
    const list = cfg.softphoneProcessAllowlist || cfg.softphone_process_allowlist || [];
    const nextAllowlist = Array.isArray(list) ? list.join('\n') : '';
    setLocalEnabled((prev) => (prev === nextEnabled ? prev : nextEnabled));
    setCaptureMode((prev) => (prev === nextCapture ? prev : nextCapture));
    setAppMode((prev) => (prev === nextAppMode ? prev : nextAppMode));
    setConsent((prev) => (prev === nextConsent ? prev : nextConsent));
    setRecordingAllowed((prev) => (prev === nextRecordingAllowed ? prev : nextRecordingAllowed));
    setAllowlistText((prev) => (prev === nextAllowlist ? prev : nextAllowlist));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => apiClient.monitoring.updateCallMonitoringConfig({
      enabled: localEnabled,
      capture_mode: captureMode,
      call_recording_app_mode: appMode,
      recording_consent_confirmed: consent,
      recording_allowed: recordingAllowed,
      softphone_process_allowlist: allowlistText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callMonitoringConfig'] });
      setSaved(true);
    },
  });

  if (!canConfigure) {
    return <Alert severity="info">Call Monitoring configuration requires call_qa.call_monitoring.configure.</Alert>;
  }
  if (isLoading) return <CircularProgress size={20} />;

  return (
    <Box>
      <FormControlLabel
        control={(
          <Switch
            checked={localEnabled}
            onChange={(e) => { setSaved(false); setLocalEnabled(e.target.checked); }}
          />
        )}
        label="Call Monitoring Enabled (Global)"
      />
      <FormControlLabel
        control={(
          <Switch
            checked={recordingAllowed}
            onChange={(e) => { setSaved(false); setRecordingAllowed(e.target.checked); }}
          />
        )}
        label="Recording Allowed"
      />
      <FormControlLabel
        control={(
          <Switch
            checked={consent}
            onChange={(e) => { setSaved(false); setConsent(e.target.checked); }}
          />
        )}
        label="Recording Policy / Disclosure Confirmed"
      />
      <TextField
        select
        size="small"
        fullWidth
        label="Default Capture Mode"
        sx={{ mt: 1 }}
        value={captureMode}
        onChange={(e) => { setSaved(false); setCaptureMode(e.target.value); }}
      >
        <MenuItem value="DESKTOP_CAPTURE">DESKTOP_CAPTURE</MenuItem>
        <MenuItem value="PROVIDER_FIRST">PROVIDER_FIRST</MenuItem>
        <MenuItem value="FALLBACK_ONLY">FALLBACK_ONLY</MenuItem>
        <MenuItem value="MANUAL_ONLY">MANUAL_ONLY</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        fullWidth
        label="Which apps to record"
        sx={{ mt: 1 }}
        value={appMode}
        onChange={(e) => { setSaved(false); setAppMode(e.target.value === 'ALLOWLIST' ? 'ALLOWLIST' : 'ALL'); }}
        helperText={
          appMode === 'ALL'
            ? 'Record calls in every app (Ignite-like). Detects real voice calls via mic + remote audio — not merely an open softphone.'
            : 'Only record when an allowlisted process is involved. Use for locked-down fleets.'
        }
      >
        <MenuItem value="ALL">Record calls in every app</MenuItem>
        <MenuItem value="ALLOWLIST">Allowlisted apps only</MenuItem>
      </TextField>
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label={appMode === 'ALL' ? 'Softphone hints (optional)' : 'Softphone Process Allowlist'}
        sx={{ mt: 1 }}
        value={allowlistText}
        onChange={(e) => { setSaved(false); setAllowlistText(e.target.value); }}
        helperText={
          appMode === 'ALL'
            ? 'Optional process names (Task Manager Details, e.g. msedge, ms-teams). Helps attribute browser dialers; not required to record. Process presence alone never records.'
            : 'One process name per line (e.g. msedge, ms-teams, goto). .exe optional. Leave empty for built-in defaults. Process presence alone never records — audio evidence is still required.'
        }
      />
      <Button
        sx={{ mt: 2 }}
        variant="contained"
        disabled={saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
      >
        Save Call Monitoring
      </Button>
      {saved && <Alert severity="success" sx={{ mt: 1 }}>Call Monitoring settings saved.</Alert>}
    </Box>
  );
};

const CallQaConfigurationPage: React.FC = () => {
  const { user } = useAuth();
  const canConfigure = (user?.permissions || []).includes('call_qa.configure');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<any>(null);
  const [providerForm, setProviderForm] = useState({
    name: '',
    provider_type: 'MANUAL',
    status: 'ACCESS_PENDING',
    recording_source_preference: 'MANUAL_ONLY',
    credential_secret_ref: '',
    notes: '',
  });
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['callQaConfiguration'],
    queryFn: () => apiClient.callQa.getConfiguration(),
    enabled: canConfigure,
  });

  useEffect(() => {
    if (data?.data?.data) setForm(data.data.data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.callQa.updateConfiguration(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaConfiguration'] });
      setSaved(true);
    },
  });

  const providerMutation = useMutation({
    mutationFn: () => apiClient.callQa.upsertProvider({
      name: providerForm.name,
      provider_type: providerForm.provider_type,
      status: providerForm.status,
      recording_source_preference: providerForm.recording_source_preference,
      credential_secret_ref: providerForm.credential_secret_ref || null,
      notes: providerForm.notes,
      capabilities: {
        supports_call_history: false,
        supports_webhooks: false,
        supports_recordings: false,
        supports_transcripts: false,
        supports_speaker_labels: false,
        supports_historical_import: false,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaConfiguration'] });
      setProviderForm({
        name: '',
        provider_type: 'MANUAL',
        status: 'ACCESS_PENDING',
        recording_source_preference: 'MANUAL_ONLY',
        credential_secret_ref: '',
        notes: '',
      });
    },
  });

  if (!canConfigure) {
    return <Alert severity="warning">Call QA configuration requires call_qa.configure.</Alert>;
  }
  if (isLoading || !form) return <CircularProgress size={24} />;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>General</Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(form.enabled)}
                  onChange={(e) => { setSaved(false); setForm((f: any) => ({ ...f, enabled: e.target.checked })); }}
                />
              )}
              label="Call QA Enabled"
            />
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Recording Retention Days"
              sx={{ mt: 1 }}
              value={form.retention_days ?? 90}
              onChange={(e) => {
                setSaved(false);
                setForm((f: any) => ({ ...f, retention_days: Number(e.target.value) }));
              }}
              helperText="Retention cleanup honors investigation holds when enabled (call_qa_retention_cleanup_enabled)."
            />
            <Box mt={2}>
              <Button
                variant="contained"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate({
                  enabled: Boolean(form.enabled),
                  retention_days: Number(form.retention_days || 90),
                  auto_transcribe: Boolean(form.auto_transcribe),
                  preferred_language: form.preferred_language || 'AUTO',
                  transcription_source_preference: form.transcription_source_preference || 'PROVIDER_IF_AVAILABLE',
                  search_preroll_ms: Number(form.search_preroll_ms || 3000),
                  auto_score: Boolean(form.auto_score),
                })}
              >
                Save Settings
              </Button>
              {saved && <Alert severity="success" sx={{ mt: 1 }}>Saved.</Alert>}
            </Box>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Storage</Typography>
            <Typography variant="body2">
              Storage configured: {form.storage_configured ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2">
              Max upload: {Math.round((form.max_upload_bytes || 0) / (1024 * 1024))} MB
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Physical storage paths are never exposed to the browser. Set CALL_QA_RECORDING_STORAGE_DIR on the server.
            </Typography>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Transcription</Typography>
            {!form.transcription_provider_configured && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                TRANSCRIPTION_PROVIDER_NOT_CONFIGURED — set CALL_QA_USE_MOCK_TRANSCRIPTION=true for demos,
                or configure CALL_QA_TRANSCRIPTION_PROVIDER with secrets (no hard-coded API keys).
              </Alert>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              Provider: {form.transcription_provider_name || 'UNCONFIGURED'}
              {form.transcription_provider_configured ? ' (configured)' : ''}
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(form.auto_transcribe)}
                  onChange={(e) => { setSaved(false); setForm((f: any) => ({ ...f, auto_transcribe: e.target.checked })); }}
                />
              )}
              label="Automatically Transcribe New Calls"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(form.auto_score)}
                  onChange={(e) => { setSaved(false); setForm((f: any) => ({ ...f, auto_score: e.target.checked })); }}
                />
              )}
              label="Automatically Score Completed Transcripts"
            />
            <Typography variant="body2" sx={{ mb: 1, mt: 1 }}>
              Scoring provider: {form.scoring_provider_name || 'UNCONFIGURED'}
              {form.scoring_provider_configured ? ' (configured)' : ''}
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              label="Preferred Language"
              sx={{ mt: 1 }}
              value={form.preferred_language || 'AUTO'}
              onChange={(e) => { setSaved(false); setForm((f: any) => ({ ...f, preferred_language: e.target.value })); }}
            >
              <MenuItem value="AUTO">AUTO</MenuItem>
              <MenuItem value="ENGLISH">ENGLISH</MenuItem>
              <MenuItem value="SPANISH">SPANISH</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              label="Transcription Source Preference"
              sx={{ mt: 1 }}
              value={form.transcription_source_preference || 'PROVIDER_IF_AVAILABLE'}
              onChange={(e) => { setSaved(false); setForm((f: any) => ({ ...f, transcription_source_preference: e.target.value })); }}
            >
              <MenuItem value="PROVIDER_IF_AVAILABLE">PROVIDER_IF_AVAILABLE</MenuItem>
              <MenuItem value="TEKS">TEKS</MenuItem>
              <MenuItem value="MANUAL_ONLY">MANUAL_ONLY</MenuItem>
            </TextField>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Search Pre-roll (ms)"
              sx={{ mt: 1 }}
              value={form.search_preroll_ms ?? 3000}
              onChange={(e) => {
                setSaved(false);
                setForm((f: any) => ({ ...f, search_preroll_ms: Number(e.target.value) }));
              }}
              helperText="Seconds before a transcript match when using Play from match (default 3000)."
            />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Call Monitoring</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Global master switch for automatic desktop call capture. Per-computer enable remains in Workforce Monitoring → Computers → Call Monitoring.
              Independent of Clock In / TEKS sign-in.
            </Typography>
            <CallMonitoringGlobalConfigCard />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Alert emails</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose which alert levels send a &ldquo;listen to this call&rdquo; email. Each level has its own color and urgency in the email.
            </Typography>
            <AlertEmailSettingsCard />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Agent Conduct Email Notifications</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Universal agent-conduct monitoring — unacceptable behavior toward customers (threats, abuse,
              harassment, deception). Separate from QA scorecards: a low QA score alone never sends a conduct
              email, and these controls do not affect scorecard results.
            </Typography>
            <ConductEmailSettingsCard />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Telephony Providers</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Real vendor adapters stay ACCESS_PENDING until verified API credentials exist (env secret refs only — never paste tokens here).
            </Typography>
            {(form.providers || []).map((p: any) => (
              <Box key={p.id} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{p.name}</strong> ({p.provider_type}) — {p.status}
                  {p.recording_source_preference ? ` · ${p.recording_source_preference}` : ''}
                </Typography>
              </Box>
            ))}
            {(form.providers || []).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No providers configured.</Typography>
            )}
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={12} md={3}>
                <TextField size="small" fullWidth label="Name" value={providerForm.name} onChange={(e) => setProviderForm((p) => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField size="small" fullWidth label="Provider type" value={providerForm.provider_type} onChange={(e) => setProviderForm((p) => ({ ...p, provider_type: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Status"
                  value={providerForm.status}
                  onChange={(e) => setProviderForm((p) => ({ ...p, status: e.target.value }))}
                >
                  {['NOT_CONFIGURED', 'ACCESS_PENDING', 'CONNECTED', 'DEGRADED', 'DISABLED', 'DESKTOP_FALLBACK', 'MANUAL_ONLY'].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Recording preference"
                  value={providerForm.recording_source_preference}
                  onChange={(e) => setProviderForm((p) => ({ ...p, recording_source_preference: e.target.value }))}
                >
                  {['PROVIDER_FIRST', 'DESKTOP_FALLBACK', 'MANUAL_ONLY'].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Credential env secret ref"
                  value={providerForm.credential_secret_ref}
                  onChange={(e) => setProviderForm((p) => ({ ...p, credential_secret_ref: e.target.value }))}
                  helperText="e.g. CALL_QA_PROVIDER_GENESYS_TOKEN — stores the env var name only"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={!providerForm.name || providerMutation.isPending}
                  onClick={() => providerMutation.mutate()}
                >
                  Save Provider
                </Button>
              </Grid>
            </Grid>
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CallQaConfigurationPage;
